import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { buyerLoginApi } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9e7963b6-8d0a-418d-8246-af7a5b4f8387/artifacts/h6dgewwm_image.png';

export default function BuyerLoginPage() {
  const { buyerLogin, isBuyer, t } = useApp();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  useEffect(() => {
    if (isBuyer) navigate('/buyer');
  }, [isBuyer, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await buyerLoginApi(form);
      buyerLogin(res.data.access_token, res.data.buyer);
      toast.success(t('buyerAuth.loginSuccess'));
      navigate('/buyer');
    } catch (err) {
      const detail = err.response?.data?.detail || t('buyerAuth.loginFailed');
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="buyer-login-page">
      <Toaster position="top-right" theme="dark" />
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <img src={LOGO_URL} alt="LUXOR TEK" className="h-12 mx-auto mb-6" />
          <h1 className="text-2xl font-bold">{t('buyerAuth.portal')}</h1>
          <p className="text-white/60 mt-2">{t('buyerAuth.signInAccess')}</p>
        </div>

        <div className="card-dark p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="buyer-login-form">
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('buyerAuth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-12 pr-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                  placeholder="buyer@company.com"
                  data-testid="buyer-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">{t('buyerAuth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                  placeholder="********"
                  data-testid="buyer-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="buyer-login-submit"
            >
              {loading ? t('buyerAuth.signingIn') : t('buyerAuth.signIn')}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="text-white/40 text-sm">
              <Link to="/forgot-password" className="text-[#00D4FF] hover:text-[#00A0FF]" data-testid="forgot-password-link">
                {t('buyerAuth.forgotPassword')}
              </Link>
            </p>
            <p className="text-white/40 text-sm">
              {t('buyerAuth.noAccount')}{' '}
              <Link to="/wholesale-register" className="text-[#00D4FF] hover:text-[#00A0FF]">
                {t('buyerAuth.registerWholesale')}
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-white/40 hover:text-white text-sm">
            &larr; {t('common.backToWebsite')}
          </Link>
        </div>
      </div>
    </div>
  );
}
